import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { errorHandleMiddleware } from '@entities/attachment';
import { COMMENT_ATTACHMENT_EXTENSIONS } from '@entities/comment';

import {
    TASK_COMMENT_MAX_FILE_SIZE_BYTES,
    TASK_COMMENT_MAX_FILES,
    TASK_COMMENT_MAX_TEXT_LENGTH,
} from './config';
import { resolveTaskCommentFormError } from './resolveTaskCommentFormError';
import { useCreateTaskCommentMutation } from '../api/useCreateTaskCommentMutation';

import type { TTaskCommentFormError } from './resolveTaskCommentFormError';
import type { TCreateTaskCommentPayload } from './types';

export const useTaskCommentForm = (taskId: string, isInitialLoading = false) => {
    const { t } = useTranslation();
    const mutation = useCreateTaskCommentMutation(taskId);
    const submittingRef = useRef(false);
    const [draft, setDraft] = useState<TCreateTaskCommentPayload>({ text: '', files: [] });
    const [isInvalid, setIsInvalid] = useState(false);
    const [formError, setFormError] = useState<TTaskCommentFormError>({
        fieldErrors: {},
        errorMessage: '',
    });

    const isPending = isInitialLoading || mutation.isPending;
    const trimmedText = draft.text.trim();
    const isValid = Boolean(trimmedText || draft.files.length) &&
        trimmedText.length <= TASK_COMMENT_MAX_TEXT_LENGTH;

    const clearFieldError = (field: keyof TCreateTaskCommentPayload) => {
        setFormError((current) => {
            const fieldErrors = { ...current.fieldErrors };
            delete fieldErrors[field];
            return { fieldErrors, errorMessage: '' };
        });
    };

    const changeText = (text: string) => {
        if (isPending || submittingRef.current) return;
        setDraft((current) => ({ ...current, text }));
        setIsInvalid(false);
        clearFieldError('text');
    };

    const handleFiles = errorHandleMiddleware({
        t,
        onAddFiles: (files) => {
            setDraft((current) => ({ ...current, files: [...current.files, ...files] }));
            setIsInvalid(false);
            clearFieldError('files');
        },
        alreadyAttachedLength: draft.files.length,
        maxFiles: TASK_COMMENT_MAX_FILES,
        // Middleware принимает MiB, а серверный лимит задан в байтах.
        maxAttachmentSizeInMb: TASK_COMMENT_MAX_FILE_SIZE_BYTES / (1024 * 1024),
        maxOverallSizeInMb:
            TASK_COMMENT_MAX_FILES * TASK_COMMENT_MAX_FILE_SIZE_BYTES / (1024 * 1024),
        validateExtensions: COMMENT_ATTACHMENT_EXTENSIONS,
        multiple: true,
    });

    const addFiles = (files: File[]) => {
        if (isPending || submittingRef.current || !files.length) return;
        handleFiles(files);
    };

    const removeFile = (file: File) => {
        if (isPending || submittingRef.current) return;
        setDraft((current) => ({
            ...current,
            files: current.files.filter((item) => item !== file),
        }));
        clearFieldError('files');
    };

    const submit = async (): Promise<void> => {
        if (isPending || submittingRef.current) return;
        if (!isValid) {
            setIsInvalid(true);
            return;
        }

        submittingRef.current = true;
        setFormError({ fieldErrors: {}, errorMessage: '' });
        try {
            await mutation.mutateAsync(draft, {
                onError: (error) => {
                    setFormError(resolveTaskCommentFormError(
                        error,
                        t('Errors.UnknownError.text'),
                        t('Errors.UploadErrorText.text')
                    ));
                },
            });
            setDraft({ text: '', files: [] });
            setIsInvalid(false);
        } catch {
            // onError устанавливает ошибки формы; черновик остаётся для повторной отправки.
        } finally {
            submittingRef.current = false;
        }
    };

    return {
        ...draft,
        ...formError,
        isPending,
        isSubmitting: mutation.isPending,
        isInvalid,
        changeText,
        addFiles,
        removeFile,
        submit,
    };
};
